import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SmartPhrase, SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import styles from './SmartPhraseChip.module.css';

type TooltipData = { value: string; isMock: boolean };

async function resolveTooltipValue(
  phrase: SmartPhrase,
  context?: SmartPhraseContext,
): Promise<TooltipData | undefined> {
  if (phrase.mockResolveValue !== undefined) {
    return { value: phrase.mockResolveValue, isMock: true };
  }

  if (!context) {
    return undefined;
  }

  try {
    const result = await phrase.resolve(context);

    if (result) {
      return { value: result, isMock: false };
    }
  } catch {
    // Resolve failed, fall through to example/display value
  }

  return undefined;
}

interface ChipProps {
  phrase: SmartPhrase;
  context?: SmartPhraseContext;
  variant: 'default' | 'admin';
}

const SmartPhraseChip = ({ phrase, context, variant }: ChipProps) => {
  const [tooltipData, setTooltipData] = useState<TooltipData>();

  const badgeClass =
    variant === 'admin' ? styles.smartPhraseAdmin : styles.smartPhrase;
  const badgeRef = useRef<HTMLSpanElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(
    null,
  );

  const showTooltip = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    setTooltipStyle({
      left: rect.left + rect.width / 2,
      top: rect.top - 6,
      transform: 'translate(-50%, -100%)',
    });
  };

  const hideTooltip = () => setTooltipStyle(null);

  useEffect(() => {
    resolveTooltipValue(phrase, context).then(data => {
      setTooltipData(data);
    });
  }, [phrase, context]);

  return (
    <>
      <span
        ref={badgeRef}
        className={badgeClass}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {phrase.display}
      </span>
      {tooltipStyle &&
        createPortal(
          <span className={styles.tooltipPortal} style={tooltipStyle}>
            {phrase.description}
            {tooltipData && (
              <>
                <span className={styles.tooltipLabel}>
                  {tooltipData.isMock ? 'Example value:' : 'Value:'}
                </span>
                <span className={styles.tooltipValue}>{tooltipData.value}</span>
              </>
            )}
          </span>,
          document.body,
        )}
    </>
  );
};

export default SmartPhraseChip;
